import ProductModel from '../features/product-model';
import {NotificationPopup} from '../features/notification-popup';

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.flkty = null;
        this.flktyNav = null;
        this.sliderResizeObserver = null;
        this.isFlickityDragging = false;
        this.mobileSliderLayoutActive = null;
        this.mobileDotsActive = null;
        this.checkSliderOnResize = () => this.checkSlider();
        this.flktyNavOnResize = () => this.resizeFlickityNav();
        this.previouslyVisibleMediaIds = new Set();

        this.quantityInput = this.querySelector('[data-quantity-field]');
      }

      connectedCallback() {
        this.sessionStorage = window.sessionStorage;
        this.hasUnitPricing = this.querySelector('[data-product-unit]');
        this.notificationPopupButton = this.querySelector('[data-notification-popup]');

        this.initializeProductSwapUtility();

        // Record recently viewed products on initial load
        try {
          const handle = this.dataset.productHandle;
          if (window.Shopify?.Products?.recordRecentlyViewed) {
            if (handle) {
              window.Shopify.Products.recordRecentlyViewed({handle});
            } else {
              window.Shopify.Products.recordRecentlyViewed();
            }
          }
        } catch (e) {
          // Silently handle recently viewed recording errors - not critical for user experience
          console.warn('Recently viewed recording error:', e);
        }

        this.onVariantChangeUnsubscriber = subscribe(theme.PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('theme:product-info:loaded', {bubbles: true}));

        this.variantOptionImages = this.querySelectorAll('[data-variant-option-image]');
        this.hasVariantOptionWithImage = this.variantOptionImages.length > 1;

        if (this.hasVariantOptionWithImage) {
          this.optionImagesWidth();
          this.onResizeCallback = () => this.resizeEvents();
          window.addEventListener('theme:resize:width', this.onResizeCallback);
        }

        this.tallLayout = this.getAttribute('data-tall-layout') === 'true';
        this.carouselImagesNumber = parseInt(this.getAttribute('data-carousel-images-number'), 10) || 1;
        this.variantImageScroll = this.getAttribute('data-variant-image-scroll') === 'true';
        this.mobileGalleryDots = this.getAttribute('data-mobile-gallery-dots') !== 'false';
        this.featureSliders = this.querySelectorAll('[data-slider]');

        this.initUpsellSlider();
        this.initFeatureSlider();

        this.productSlider();
        this.initMediaSwitch();
        this.initShopifyXrLaunch();

        if (this.notificationPopupButton) {
          this.setNotificationPopup(this.notificationPopupButton);
        }

        this.checkLiveCartInfo();
        this.checkLiveCartInfoCallback = () => this.checkLiveCartInfo();
        document.addEventListener('theme:cart:close', this.checkLiveCartInfoCallback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('[data-quantity-wrapper]');
        if (!this.quantityForm) return;

        this.setQuantityBoundaries();
        // Update button price and cart limits when quantity changes
        this.quantityInput.addEventListener('change', () => {
          this.updateProductPrices();
          this.checkLiveCartInfo();
        });
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(theme.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();

          // Call optionImagesWidth after variant change HTML updates
          this.optionImagesWidth(newNode);
        });
      }

      optionImagesWidth() {
        const variantOptionImages = this.querySelectorAll('[data-variant-option-image]');

        if (variantOptionImages.length > 1) {
          const variantButtonsContainer = variantOptionImages[0].closest('[data-variant-buttons]');

          requestAnimationFrame(() => {
            const heights = [...variantOptionImages].map((item) => Math.floor(item.offsetHeight));
            const widths = [...variantOptionImages].map((item) => Math.floor(item.offsetWidth));
            const widest = Math.max(...widths);
            const tallest = Math.max(...heights);

            variantButtonsContainer.style.setProperty('--option-image-width', widest + 'px');
            variantButtonsContainer.style.setProperty('--option-image-height', tallest + 'px');
          });
        }
      }

      resizeEvents() {
        this.optionImagesWidth();
      }

      handleOptionValueChange({data: {event, target, selectedOptionValues}}) {
        if (!this.contains(event.target)) return;

        this.isCartBarOptionChange = Boolean(event?.fromCartBar);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : this.handleUpdateProductInfo(productUrl),
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(document.querySelector('main'), html.querySelector('main'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          } else {
            HTMLUpdateUtility.viewTransition(this, html.querySelector('product-info'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);

            // Element may persist after in-place swap — clear pending and sync ATC from swapped variant
            this.setVariantUpdatePending(false);
            if (!variant) {
              this.setUnavailable();
            } else {
              this.productState = this.setProductState(variant);
              this.updateAddToCartState(variant);
            }
          }

          // Ensure optionImagesWidth is called after the swap
          requestAnimationFrame(() => this.optionImagesWidth());
        };
      }

      renderProductInfo({requestUrl, targetId, callback}) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.setVariantUpdatePending(true);

        fetch(requestUrl, {signal: this.abortController.signal})
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (!this.isCartBarOptionChange) {
              document.querySelector(`#${targetId}`)?.focus({preventScroll: true});
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              // Keep ATC disabled — a newer variant request is in flight
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      /**
       * Gate ATC / Buy It Now while the async variant section request is in flight
       * so a fast click cannot submit the previous variant ID.
       * Buttons stay visible (greyed out) — Buy It Now is not removed to avoid layout jump/re-animation.
       * Clearing pending does not re-enable ATC — callers restore via updateAddToCartState / setUnavailable.
       */
      setVariantUpdatePending(isPending) {
        this.classList.toggle('is-variant-loading', isPending);

        if (!isPending) return;

        this.querySelectorAll('[data-add-to-cart]:not([data-upsell-btn])').forEach((element) => {
          element.disabled = true;
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');

        // Force aos-animate class without animation
        variantSelects?.querySelectorAll('[data-aos]')?.forEach((element) => element.classList.add('aos-animate', 'no-animation'));

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const selectedVariantScript = this.querySelector('[data-selected-variant]');
          if (selectedVariantScript) {
            selectedVariantScript.textContent = variant ? JSON.stringify(variant) : '';
          }

          if (!variant) {
            // Clear pending after ID update so ATC is not briefly enabled with a stale ID
            this.setVariantUpdatePending(false);
            this.setUnavailable();
            publish(theme.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant: null,
              },
            });
            return;
          }

          this.updateMedia(variant.featured_media);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            let sectionId = `${this.sectionId}-${this.dataset.productId}`;

            const source = html.getElementById(`${id}-${sectionId}`);
            const destination = this.querySelector(`#${id}-${sectionId}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          this.productState = this.setProductState(variant);

          updateSourceFromDestination('Price');
          this.updateTitleAttr(variant);
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('AddToCartText');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PreorderProperty');

          // Clear pending after ID is updated, then restore ATC for the new variant
          this.setVariantUpdatePending(false);
          // Update state AFTER innerHTML updates to avoid element disconnection issues
          this.updateAddToCartState(variant);

          // Update quantity rules when variant changes
          this.updateQuantityRules(this.sectionId, html);

          // Reset quantity to 1 on option change and sync any dropdown/display
          if (this.quantityInput) {
            this.quantityInput.value = 1;
            const quantityDropdown = this.querySelector('[data-quantity-select]');
            if (quantityDropdown) quantityDropdown.innerText = '1';
            // Sync cart bar qty; prices updated explicitly below
            this.quantityInput.dispatchEvent(new Event('input', {bubbles: true}));
          }

          // Check live cart info for new variant to update inventory limits
          this.checkLiveCartInfo();

          // Recalculate button price after DOM updates
          this.updateProductPrices(variant);

          // Update variant option image widths after DOM updates
          this.optionImagesWidth();

          // Dispatch custom event after all price innerHTML updates are complete
          this.dispatchEvent(
            new CustomEvent('theme:variant:prices-updated', {
              bubbles: true,
              detail: {variant, sectionId: this.sectionId},
            })
          );

          publish(theme.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // Construct the full section ID (includes product ID for quickviews)
        const sectionId = `${this.sectionId}-${this.dataset.productId}`;

        this.querySelectorAll(`#AddToCartForm--${sectionId}`).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', {bubbles: true}));
        });
      }

      updateURL(url, variantId) {
        this.shareButton?.updateUrl(`${window.theme.routes.shop_url}${url}${variantId ? `?variant=${variantId}` : ''}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const remainingWrapper = this.querySelector('[data-remaining-wrapper]');
        this.productForm?.toggleSubmitButton(true, theme.strings.unavailable);

        this.querySelectorAll('[data-price-wrapper]').forEach((element) => {
          // Hide price if there is no variant
          if (element.hasAttribute('data-atc-button')) return; // Skip if the price is on the ATC button
          element.classList.add('product__price--hidden');
        });

        if (remainingWrapper) {
          remainingWrapper.classList.remove('count-is-in', 'count-is-out', 'count-is-low');
          remainingWrapper.classList.add('count-is-unavailable');
        }
      }

      setProductState(variant) {
        const productState = {
          available: true,
          soldOut: false,
          onSale: false,
          showUnitPrice: false,
        };

        if (!variant) {
          productState.available = false;
        } else {
          if (!variant.available) {
            productState.soldOut = true;
          }

          if (variant.compare_at_price > variant.price) {
            productState.onSale = true;
          }

          if (variant.unit_price) {
            productState.showUnitPrice = true;
          }
        }
        return productState;
      }

      updateMedia(variantFeaturedMedia) {
        if (!variantFeaturedMedia) return;

        // Update variant image, if one is set
        const newImg = this.querySelector(`[data-product-image][data-image-id="${variantFeaturedMedia.id}"]`);
        const newImageParent = newImg?.closest('[data-product-slide]');

        if (newImageParent) {
          const newImagePos = parseInt([...newImageParent.parentElement.children].indexOf(newImageParent));
          const imgSlider = this.querySelector('[data-product-single-media-slider]');
          const flkty = window.theme.Flickity.data(imgSlider);

          // Activate image slide in mobile view
          if (flkty && flkty.isActive) {
            const variantSlide = imgSlider.querySelector(`[data-id="${variantFeaturedMedia.id}"]`);

            if (variantSlide) {
              this.hideTooltipOnSlideChange(variantSlide);
              const slideIndex = parseInt([...variantSlide.parentNode.children].indexOf(variantSlide));
              flkty.select(slideIndex);
            }
            return;
          }

          if (this.tallLayout && this.variantImageScroll) {
            // We know its a tall layout, if it's sticky
            // scroll to the images
            // Scroll to/reorder image unless it's the first photo on load

            // Skip scrolling if the option change came from cart-bar
            if (this.isCartBarOptionChange) {
              return;
            }

            const newImgTop = newImg.getBoundingClientRect().top;

            if (newImagePos === 0 && newImgTop + window.scrollY > window.pageYOffset) return;

            // Scroll to variant image
            window.theme.scrollTo(newImgTop, newImageParent);
            window.theme.closeAllTooltips();
          }
        }
      }

      updateTitleAttr(variant) {
        if (variant.name) {
          const form = this.productForm?.querySelector('form');
          form?.setAttribute('data-variant-title', variant.name);
        }
      }

      get productForm() {
        return this.querySelector('product-form');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get shareButton() {
        return this.querySelector('share-button');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      productSlider() {
        this.checkSlider();
        document.addEventListener('theme:resize:width', this.checkSliderOnResize);
      }

      checkSlider() {
        const isMobileWidth = window.innerWidth < theme.sizes.large;
        // Mobile peek-slider layout only below 768px (not tablet 768–1023)
        const isMobileSliderWidth = window.innerWidth < theme.sizes.small;

        if (this.tallLayout && !isMobileWidth) {
          this.destroyProductSlider();
          return;
        }

        if (!this.tallLayout && !isMobileWidth) {
          const mediaCount = this.querySelectorAll('[data-product-single-media-wrapper]').length;
          if (mediaCount <= this.carouselImagesNumber) {
            this.destroyProductSlider();
            return;
          }
        }

        const useMobileSliderLayout = this.dataset.mobileGallery === 'slider' && isMobileSliderWidth;
        const needsMobileDots = useMobileSliderLayout && this.mobileGalleryDots;

        if (this.flkty !== null) {
          if (this.mobileSliderLayoutActive !== useMobileSliderLayout || this.mobileDotsActive !== needsMobileDots) {
            this.destroyProductSlider();
          } else {
            if (this.sliderResizeObserver === null) this.observeSliderWidth(this.flkty.element);
            return;
          }
        }

        this.mobileSliderLayoutActive = useMobileSliderLayout;
        this.mobileDotsActive = needsMobileDots;
        this.initProductSlider();
      }

      resizeFlickityNav() {
        if (this.flktyNav !== null) {
          this.flktyNav.resize();
        }
      }

      /* Product Slider */
      initProductSlider() {
        const slider = this.querySelector('[data-product-single-media-slider]');
        const thumbs = this.querySelector('[data-product-single-media-thumbs]');
        const media = this.querySelectorAll('[data-product-single-media-wrapper]');
        const gallery = this.querySelector('[data-product-single-media-group]');
        const mobileSliderGallery = this.dataset.mobileGallery === 'slider';
        const useMobileSliderLayout = mobileSliderGallery && window.innerWidth < theme.sizes.small;
        const useMobileSliderDots = useMobileSliderLayout && this.mobileGalleryDots;
        const useThumbnailNav = thumbs !== null && !useMobileSliderLayout;
        let adaptiveHeight = true;
        let cellAlign = 'center';

        if (useMobileSliderLayout) {
          cellAlign = 'left';
        } else if (this.closest('.popup-quick-view')) {
          cellAlign = 'left';
        } else if (gallery && (gallery.classList.contains('product-gallery--two') || gallery.classList.contains('product-gallery--three'))) {
          adaptiveHeight = false;
          cellAlign = 'left';
        }

        if (media.length > 1) {
          this.flkty = new window.theme.Flickity(slider, {
            wrapAround: true,
            pageDots: useMobileSliderDots,
            adaptiveHeight: adaptiveHeight,
            cellAlign: cellAlign,
            on: {
              ready: () => {
                this.slidesAriaHidden(slider);

                media.forEach((item) => {
                  if (!item.classList.contains('is-selected')) {
                    const links = item.querySelectorAll('a, button');
                    if (links.length) {
                      links.forEach((link) => {
                        link.setAttribute('tabindex', '-1');
                      });
                    }
                  }
                });

                // Set initial visibility state for visible slides
                // Use requestAnimationFrame to ensure this.flkty is assigned
                requestAnimationFrame(() => {
                  const initialSlide = this.flkty?.selectedElement;
                  const initialMediaId = initialSlide?.getAttribute('data-media-id');
                  if (initialMediaId) {
                    this.switchMedia(initialMediaId);
                  }
                });
              },
              dragStart: () => {
                slider.classList.add('is-moving');
              },
              dragMove: () => {
                // Prevent lightbox trigger on dragMove
                this.isFlickityDragging = true;
              },
              staticClick: () => {
                this.isFlickityDragging = false;
              },
              change: () => {
                // Fix aria-hidden error
                this.slidesAriaHidden(slider);
              },
              settle: (index) => {
                const currentSlide = this.flkty.selectedElement;
                const mediaId = currentSlide.getAttribute('data-media-id');

                this.flkty.cells.forEach((slide, i) => {
                  const links = slide.element.querySelectorAll('a, button');
                  if (links.length) {
                    links.forEach((link) => {
                      link.setAttribute('tabindex', i === index ? '0' : '-1');
                    });
                  }
                });

                // Use requestAnimationFrame to ensure Flickity positions are fully updated
                requestAnimationFrame(() => {
                  this.switchMedia(mediaId);
                });
                slider.classList.remove('is-moving');
              },
            },
          });

          // Toggle flickity draggable functionality based on media play/pause state
          if (media.length) {
            media.forEach((el) => {
              el.addEventListener('theme:media:play', () => {
                this.flkty.options.draggable = false;
                this.flkty.updateDraggable();
                el.closest('[data-product-single-media-slider]').classList.add('has-media-active');
              });

              el.addEventListener('theme:media:pause', () => {
                this.flkty.options.draggable = true;
                this.flkty.updateDraggable();
                el.closest('[data-product-single-media-slider]').classList.remove('has-media-active');
              });
            });
          }

          // iOS smooth scrolling fix
          window.theme.flickitySmoothScrolling(slider);

          this.observeSliderWidth(slider);

          if (useThumbnailNav) {
            this.flktyNav = new window.theme.Flickity(thumbs, {
              asNavFor: slider,
              contain: true,
              pageDots: false,
              prevNextButtons: false,
              resize: true,
              on: {
                ready: () => {
                  thumbs.setAttribute('tabindex', '-1');
                  this.slidesAriaHidden(thumbs);
                  requestAnimationFrame(this.flktyNavOnResize);
                },
                change: () => {
                  this.slidesAriaHidden(thumbs);
                },
              },
            });

            const onTransitionEnd = () => {
              requestAnimationFrame(this.flktyNavOnResize);
              this.flktyNav.viewport.removeEventListener('transitionend', onTransitionEnd);
            };

            if (this.flktyNav !== null) {
              document.addEventListener('theme:resize:width', this.flktyNavOnResize);
              this.flktyNav.viewport.addEventListener('transitionend', onTransitionEnd);
            }

            // iOS smooth scrolling fix
            window.theme.flickitySmoothScrolling(thumbs);
          }
        }
      }

      observeSliderWidth(slider) {
        if (!('ResizeObserver' in window)) return;

        let sliderWidth = slider.offsetWidth;

        this.sliderResizeObserver = new ResizeObserver(() => {
          if (this.flkty === null || slider.offsetWidth === sliderWidth) return;

          sliderWidth = slider.offsetWidth;

          requestAnimationFrame(() => {
            this.flkty?.resize();
            this.flktyNav?.resize();
          });
        });
        this.sliderResizeObserver.observe(slider);
      }

      slidesAriaHidden(slider) {
        const sliderNav = window.theme.Flickity.data(slider);

        sliderNav.cells.forEach((slide) => slide.element.setAttribute('aria-hidden', 'false'));
      }

      destroyProductSlider() {
        this.sliderResizeObserver?.disconnect();
        this.sliderResizeObserver = null;

        if (this.flkty !== null) {
          this.flkty.destroy();
          this.flkty = null;
        }

        if (this.flktyNav !== null) {
          this.flktyNav.destroy();
          this.flktyNav = null;
        }

        this.mobileSliderLayoutActive = null;
        this.mobileDotsActive = null;
      }

      /* Upsell Products Slider */
      initUpsellSlider() {
        const slider = this.querySelector('[data-upsell-slider]');
        const items = this.querySelectorAll('[data-upsell-holder]');

        if (items.length > 1) {
          const flktyUpsell = new window.theme.Flickity(slider, {
            wrapAround: true,
            pageDots: true,
            adaptiveHeight: true,
            prevNextButtons: false,
            on: {
              ready: () => {
                items.forEach((item) => {
                  if (!item.classList.contains('is-selected')) {
                    const links = item.querySelectorAll('a, button');
                    if (links.length) {
                      links.forEach((link) => {
                        link.setAttribute('tabindex', '-1');
                      });
                    }
                  }
                });
              },
            },
          });

          flktyUpsell.on('change', (index) => {
            flktyUpsell.cells.forEach((slide, i) => {
              const links = slide.element.querySelectorAll('a, button');
              if (links.length) {
                links.forEach((link) => {
                  link.setAttribute('tabindex', i === index ? '0' : '-1');
                });
              }
            });
          });
        }
      }

      /* Feature Block Slider */
      initFeatureSlider() {
        this.featureSliders.forEach((featureSliders) => {
          const featureSlideIndex = Array.from(featureSliders.children);

          if (featureSlideIndex.length > 1) {
            const flktyFeature = new window.theme.Flickity(featureSliders, {
              wrapAround: true,
              pageDots: true,
              adaptiveHeight: true,
              prevNextButtons: false,
              on: {
                ready: function () {
                  featureSlideIndex.forEach((item) => {
                    if (!item.classList.contains('is-selected')) {
                      const links = item.querySelectorAll('a, button');
                      if (links.length) {
                        links.forEach((link) => {
                          link.setAttribute('tabindex', '-1');
                        });
                      }
                    }
                  });
                },
              },
            });

            flktyFeature.on('change', (index) => {
              featureSlideIndex.forEach((slide, i) => {
                const links = slide.querySelectorAll('a, button');

                if (links.length) {
                  links.forEach((link) => {
                    link.setAttribute('tabindex', i === index ? '0' : '-1');
                  });
                }
              });
            });
          }
        });
      }

      handleMediaFocus(event) {
        const isActivationKey = event.code === theme.keyboardKeys.ENTER || event.code === theme.keyboardKeys.NUMPADENTER || event.code === theme.keyboardKeys.SPACE;

        if (!isActivationKey && event.code !== theme.keyboardKeys.TAB) {
          return;
        }

        const mediaId = event.currentTarget.getAttribute('data-id');
        const activeSlide = this.querySelector(`[data-product-single-media-wrapper][data-id="${mediaId}"]`);

        if (!activeSlide) {
          return;
        }

        const slideIndex = [...activeSlide.parentNode.children].indexOf(activeSlide);
        const slider = this.querySelector('[data-product-single-media-slider]');
        const sliderNav = this.querySelector('[data-product-single-media-thumbs]');
        const flkty = window.theme.Flickity.data(slider) || null;
        const flktyNav = window.theme.Flickity.data(sliderNav) || null;

        // Go to the related slide media
        if (flkty && flkty.isActive && slideIndex > -1 && isActivationKey) {
          flkty.select(slideIndex);
        }

        // Move thumbs to the selected one
        if (flktyNav && flktyNav.isActive && slideIndex > -1) {
          flktyNav.select(slideIndex);
        }
      }

      switchMedia(mediaId) {
        const slider = this.querySelector('[data-product-single-media-slider]');
        const mediaItems = slider ? slider.querySelectorAll('[data-product-single-media-wrapper]') : [];
        const selectedMedia = this.querySelector(`[data-product-single-media-wrapper][data-media-id="${mediaId}"]`);
        const isFocusEnabled = !document.body.classList.contains('no-outline');

        if (!selectedMedia) return;

        // Get visible slides if Flickity is active and multiple slides are showing
        const visibleMediaIds = new Set();
        if (this.flkty && this.flkty.isActive) {
          // Use getBoundingClientRect to accurately detect visible cells
          // This properly handles wrap-around in multi-column layouts
          const viewportRect = this.flkty.viewport.getBoundingClientRect();
          const viewportLeft = viewportRect.left;
          const viewportRight = viewportRect.right;

          this.flkty.cells.forEach((cell) => {
            const cellElement = cell.element;
            const cellRect = cellElement.getBoundingClientRect();
            const cellMediaId = cellElement.getAttribute('data-media-id');

            // Add a small tolerance to handle floating point precision issues
            const tolerance = 1;

            // A cell is visible if any part of it overlaps with the viewport
            // Check if the cell's right edge is past the viewport's left edge
            // AND the cell's left edge is before the viewport's right edge
            const isVisible = cellRect.right > viewportLeft - tolerance && cellRect.left < viewportRight + tolerance;

            if (isVisible && cellMediaId) {
              visibleMediaIds.add(cellMediaId);
            }
          });
        } else {
          // If Flickity is not active, only show the selected media
          visibleMediaIds.add(mediaId);
        }

        // First, dispatch hidden events to media that were visible but are no longer
        this.previouslyVisibleMediaIds.forEach((prevMediaId) => {
          if (!visibleMediaIds.has(prevMediaId)) {
            const media = this.querySelector(`[data-product-single-media-wrapper][data-media-id="${prevMediaId}"]`);
            if (media) {
              media.dispatchEvent(new CustomEvent('theme:media:hidden', {bubbles: true}));
              media.classList.add('media--hidden');
            }
          }
        });

        // Then update visibility for all media
        if (mediaItems.length) {
          mediaItems.forEach((media) => {
            const currentMediaId = media.getAttribute('data-media-id');
            const shouldBeVisible = visibleMediaIds.has(currentMediaId);
            const isMainSelected = currentMediaId === mediaId;
            const wasVisible = this.previouslyVisibleMediaIds.has(currentMediaId);

            if (!shouldBeVisible) {
              // Media is not visible in viewport - hide and pause it
              if (!wasVisible) {
                media.dispatchEvent(new CustomEvent('theme:media:hidden', {bubbles: true}));
              }
              media.classList.add('media--hidden');
            } else if (isMainSelected) {
              // Media is visible AND selected - show and play it
              media.classList.remove('media--hidden');
              media.dispatchEvent(new CustomEvent('theme:media:visible', {bubbles: true}));
            } else {
              // Media is visible but NOT selected - show it but pause it
              media.classList.remove('media--hidden');
              media.dispatchEvent(new CustomEvent('theme:media:hidden', {bubbles: true}));
            }
          });
        }

        // Update the tracking set for next time
        this.previouslyVisibleMediaIds = new Set(visibleMediaIds);

        // Focus on the main selected media
        if (isFocusEnabled) {
          selectedMedia.focus();
        }

        selectedMedia.closest('[data-product-single-media-slider]').classList.remove('has-media-active');

        // If media is not loaded, trigger poster button click to load it
        const deferredMedia = selectedMedia.querySelector('[data-deferred-media]');
        if (deferredMedia && deferredMedia.getAttribute('loaded') !== 'true') {
          selectedMedia.querySelector('[data-deferred-media-button]')?.dispatchEvent(new Event('click'));
        }
      }

      hideTooltipOnSlideChange(targetSlide) {
        // Check if there's a visible tooltip
        const tooltipContainer = document.querySelector('[data-tooltip-container]');
        if (!tooltipContainer || !tooltipContainer.classList.contains('is-visible')) {
          return;
        }

        // Get current slide and target slide heights
        const currentSlide = this.flkty?.selectedElement;
        if (!currentSlide || !targetSlide) {
          return;
        }

        const currentSlideHeight = currentSlide.offsetHeight;
        const targetSlideHeight = targetSlide.offsetHeight;
        const heightDifference = Math.abs(targetSlideHeight - currentSlideHeight);

        // Only close tooltips if there's a significant height difference that could cause layout shift
        if (heightDifference > 20) {
          // Close tooltips instantly to prevent user from seeing misplaced tooltips during layout shift
          window.theme.closeAllTooltips(true);
        }
      }

      initMediaSwitch() {
        const productThumbImages = this.querySelectorAll('[data-thumbnail-id]');
        if (productThumbImages.length) {
          productThumbImages.forEach((el) => {
            el.addEventListener('keydown', (e) => {
              if (e.code === theme.keyboardKeys.SPACE) {
                e.preventDefault();
              }
            });
            el.addEventListener('keyup', this.handleMediaFocus.bind(this));
            el.addEventListener('click', (e) => {
              e.preventDefault();
            });
          });
        }
      }

      initShopifyXrLaunch() {
        document.addEventListener('shopify_xr_launch', () => {
          const currentMedia = this.querySelector(`[data-model]:not(.media--hidden)`);
          currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
        });
      }

      updateAddToCartState(variant) {
        const priceWrapper = this.querySelectorAll('[data-price-wrapper]');
        const addToCart = this.querySelectorAll('[data-add-to-cart]');
        const formWrapper = this.querySelectorAll('[data-form-wrapper]');
        const buyItNow = this.querySelector('[data-buy-it-now]');

        // Note: This function is only called when a variant exists (setUnavailable handles the no-variant case)
        const isSoldOut = this.productState.soldOut;

        // Price wrapper elements - handle sale pricing display
        priceWrapper?.forEach((element) => {
          // ATC button price is managed by updateProductPrices() (subscriptions, quantity)
          if (element.hasAttribute('data-atc-button')) return;

          const comparePriceEl = element.querySelector('[data-compare-price]');
          const productPriceEl = element.querySelector('[data-product-price]');

          if (comparePriceEl && productPriceEl) {
            if (this.productState.onSale) {
              comparePriceEl.classList.remove('hidden');
              productPriceEl.classList.add('product__price--sale');
            } else {
              comparePriceEl.classList.add('hidden');
              productPriceEl.classList.remove('product__price--sale');
            }
          }

          element.classList.toggle('product__price--hidden', !variant);
        });

        // ATC Button elements - handle button state
        addToCart?.forEach((element) => {
          // Skip the upsell "add to cart" button
          if (element.matches('[data-upsell-btn]')) return;

          buyItNow?.classList.remove('hidden');
          element.disabled = false;

          if (isSoldOut) {
            // Sold out state - variant exists but not available
            buyItNow?.classList.add('hidden');
          }

          // Notification popup
          if (element.hasAttribute('data-notification-popup')) {
            this.setNotificationPopup(element);
          }
        });

        // Form wrapper elements - handle form state classes
        formWrapper?.forEach((element) => {
          const formSelect = element.querySelector('[data-product-select]');
          if (formSelect) {
            formSelect.value = variant.id;
          }

          if (isSoldOut) {
            // Sold out state
            element.classList.add('variant--soldout');
            element.classList.remove('variant--unavailable');
          } else {
            // Available state
            element.classList.remove('variant--soldout', 'variant--unavailable');
          }
        });
      }

      getSubscriptionUnitPrices(atcButton) {
        if (!atcButton?.dataset.sellingPlanId) return null;

        const subPriceStr = atcButton.dataset.subscriptionPrice;
        if (subPriceStr === '' || subPriceStr == null) return null;

        const price = parseInt(subPriceStr, 10);
        if (isNaN(price)) return null;

        let comparePrice = null;
        const subCompareStr = atcButton.dataset.subscriptionCompareAtPrice;
        if (subCompareStr) {
          const parsedCompare = parseInt(subCompareStr, 10);
          if (!isNaN(parsedCompare)) {
            comparePrice = parsedCompare;
          }
        }

        return {price, comparePrice};
      }

      updateProductPrices(variant) {
        variant = variant || this.getSelectedVariant(this) || null;
        if (!variant) return;

        // Update data attributes on form wrapper for subscription price updater
        const formWrapper = this.querySelector('[data-form-wrapper]');
        if (formWrapper) {
          formWrapper.dataset.variantPrice = variant.price.toString();
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            formWrapper.dataset.variantComparePrice = variant.compare_at_price.toString();
          } else {
            delete formWrapper.dataset.variantComparePrice;
          }
        }

        const priceWrappers = this.querySelectorAll('[data-price-wrapper]');
        const atcButton = this.querySelector('[data-add-to-cart]:not([data-upsell-btn])');
        const subscriptionUnitPrices = this.getSubscriptionUnitPrices(atcButton);
        const quantity = this.quantityInput ? parseInt(this.quantityInput.value || '1', 10) || 1 : 1;

        priceWrappers.forEach((wrap) => {
          const comparePriceEl = wrap.querySelector('[data-compare-price]');
          const productPriceEl = wrap.querySelector('[data-product-price]');
          const isAtcButton = wrap.hasAttribute('data-atc-button');

          let price = variant.price;
          let comparePrice = variant.compare_at_price;

          if (subscriptionUnitPrices) {
            price = subscriptionUnitPrices.price;
            comparePrice = subscriptionUnitPrices.comparePrice;
          }

          if (isAtcButton) {
            price *= quantity;
            if (comparePrice != null && comparePrice > 0) {
              comparePrice *= quantity;
            }
          }

          const showCompare = comparePrice != null && comparePrice > price;

          if (comparePriceEl) {
            if (showCompare) {
              comparePriceEl.innerHTML = theme.settings.currency_code_enable
                ? window.theme.formatMoney(comparePrice, theme.moneyWithCurrencyFormat)
                : window.theme.formatMoney(comparePrice, theme.moneyFormat);
              comparePriceEl.classList.remove('hidden');
              comparePriceEl.style.display = '';
            } else {
              comparePriceEl.classList.add('hidden');
            }
          }

          if (productPriceEl) {
            if (price === 0) {
              productPriceEl.innerHTML = window.theme.strings.free;
            } else {
              productPriceEl.innerHTML = theme.settings.currency_code_enable ? window.theme.formatMoney(price, theme.moneyWithCurrencyFormat) : window.theme.formatMoney(price, theme.moneyFormat);
            }

            if (showCompare) {
              productPriceEl.classList.add('product__price--sale');
            } else {
              productPriceEl.classList.remove('product__price--sale');
            }
          }
        });

        if (this.hasUnitPricing) {
          this.updateProductUnits(variant);
        }
      }

      updateProductUnits(variant) {
        let unitPrice = null;

        if (variant && variant.unit_price) {
          unitPrice = variant.unit_price;
        }

        if (unitPrice) {
          const base = this.getBaseUnit(variant);
          const formattedPrice = unitPrice === 0 ? window.theme.strings.free : window.theme.formatMoney(unitPrice, theme.moneyFormat);
          this.querySelector('[data-product-unit-price]').innerHTML = formattedPrice;
          this.querySelector('[data-product-base]').innerHTML = base;
          this.querySelector('[data-product-unit]').classList.remove('hidden');
        } else {
          this.querySelector('[data-product-unit]').classList.add('hidden');
        }
      }

      getBaseUnit(variant) {
        return variant.unit_price_measurement.reference_value === 1
          ? variant.unit_price_measurement.reference_unit
          : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
      }

      setQuantityBoundaries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        // Update dropdown quantity selector if it exists
        const quantityDropdown = this.querySelector('[data-quantity-select]');
        if (quantityDropdown) {
          quantityDropdown.innerText = min;
        }

        publish(theme.PUB_SUB_EVENTS.quantityUpdate, undefined);
        // Ensure button price reflects current quantity boundaries
        this.updateProductPrices();
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.querySelector('input[name="id"]')?.value;
        if (!currentVariantId) return;

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => {
            console.warn('Failed to fetch quantity rules:', e);
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) {
          // If form not found in HTML, just reset boundaries with current attributes
          this.setQuantityBoundaries();
          return;
        }

        const selectors = ['[data-quantity-field]', '[data-quantity-rules]'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '[data-quantity-field]') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        // Now apply the updated attributes by setting quantity boundaries
        this.setQuantityBoundaries();
      }

      checkLiveCartInfo() {
        const variantIdInput = this.productForm?.querySelector('[name=id]');
        const variantId = variantIdInput ? variantIdInput.value : null;
        if (!variantId) return;

        const productUrl = `${theme.routes.root}products/${this.dataset.productHandle}?section_id=api-live-cart-info&variant=${variantId}`;
        const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;

        fetch(productUrl)
          .then((response) => response.text())
          .then((data) => {
            const markup = new DOMParser().parseFromString(data, 'text/html');
            const itemCountForVariant = Number(markup.querySelector('[data-item-count-for-variant]').innerHTML);
            const maxInventory = markup.querySelector('[data-max-inventory]').innerHTML;
            const maxInventoryCount = Number(maxInventory);
            const addingMoreThanAvailable = Boolean(quantity + itemCountForVariant > maxInventoryCount);
            const maxInventoryReached = maxInventory !== '' ? addingMoreThanAvailable : false;
            const errorMessagePosition = maxInventory !== '' && itemCountForVariant === maxInventoryCount ? 'form' : 'cart';

            const form = this.querySelector('[data-product-form]');
            if (!form) return;

            form.setAttribute('data-max-inventory-reached', maxInventoryReached);
            form.setAttribute('data-error-message-position', errorMessagePosition);
          })
          .catch((error) => console.log('error: ', error));
      }

      setNotificationPopup(button) {
        const variantIdInput = this.productForm?.variantIdInput;
        let variantId = variantIdInput ? variantIdInput.value : null;
        if (!variantId) return;

        let notificationFormSubmitted = false;
        const productInfo = this.closest('product-info');
        const notificationFormId = `NotificationForm--api-notification`;
        const formID = this.sessionStorage.getItem('notification_form_id');

        if (formID) {
          const sessionId = formID.substring(0, formID.lastIndexOf('--'));
          const sessionVariantId = formID.split('--').slice(-1)[0];
          notificationFormSubmitted = notificationFormId === sessionId;

          if (notificationFormSubmitted) {
            variantId = Number(sessionVariantId);
          }
        }

        // Set the handle and variant ID for the notification popup
        button.setAttribute('data-handle', productInfo?.dataset.productHandle);
        button.setAttribute('data-variant-id', variantId);

        if (notificationFormSubmitted) {
          this.scrollToForm(this.closest('.shopify-section'));
          new NotificationPopup(button);
        }
      }

      /**
       * Scroll to the last submitted notification form
       */
      scrollToForm(section) {
        const headerHeight = document.querySelector('[data-site-header]')?.dataset.height;
        const isVisible = visibilityHelper.isElementPartiallyVisible(section) || visibilityHelper.isElementTotallyVisible(section);

        if (!isVisible) {
          setTimeout(() => {
            const rect = section.getBoundingClientRect();
            const sectionTop = rect.top - headerHeight;

            window.scrollTo({
              top: sectionTop,
              left: 0,
              behavior: 'smooth',
            });
          }, 400);
        }
      }

      disconnectedCallback() {
        if (this.hasVariantOptionWithImage) window.removeEventListener('theme:resize:width', this.onResizeCallback);
        if (this.flktyNav !== null) document.removeEventListener('theme:resize:width', this.flktyNavOnResize);

        document.removeEventListener('theme:resize:width', this.checkSliderOnResize);
        this.sliderResizeObserver?.disconnect();
        this.sliderResizeObserver = null;

        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        document.removeEventListener('theme:cart:close', this.checkLiveCartInfoCallback);
      }
    }
  );
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
